/**
 * Auto-discovery of kits.
 *
 * A kit is a "Nemo page area" pack: real-Nemo pointers, screenshots,
 * reusable mocks/components, and one or more reference prototypes.
 *
 * Each kit must:
 *   - live in `src/kits/<area>/`
 *   - export a default `manifest: KitManifest` from `src/kits/<area>/manifest.ts`
 *
 * The slug is derived from the directory name.
 */

export type KitStatus = 'planned' | 'partial' | 'ready';

/**
 * Which prototyping context this kit belongs to. Mirrors
 * `PrototypeContext` from `discoverPrototypes.ts` — a kit's
 * `nemoSources` must point at the real frontend repo for that context
 * (size2mix-next-frontend, or the Urban Map frontend repo).
 */
export type KitContext = 'size2mix' | 'urbanmap';

export interface KitView {
    /** Stable id within the kit. */
    id: string;
    /** Human label, in French. */
    label: string;
    /** What this view represents in Nemo. */
    description: string;
    /** Optional path to the prototype that demonstrates this view. */
    prototypeSlug?: string;
    /** Screenshot file name inside `kits/<area>/screenshots/`. */
    screenshot?: string;
}

export interface NemoSourceRef {
    /** Human label. */
    label: string;
    /** Workspace-relative path in size2mix-next-frontend (or other repo). */
    path: string;
    /** Optional one-line note on what to look at. */
    note?: string;
}

export interface KitManifest {
    /** Stable kit id, kebab-case. Must match the directory name. */
    slug: string;
    /** Which prototyping context this kit belongs to. */
    context: KitContext;
    /** Human label, in French. */
    name: string;
    /** One-line description of the Nemo area covered. */
    description: string;
    /** Maturity. */
    status: KitStatus;
    /** Owner of the kit (firstname.lastname). */
    owner?: string;
    /** Tags for search. */
    tags: string[];
    /** Pointers to the real Nemo code/docs. */
    nemoSources: NemoSourceRef[];
    /** Shared studio components used by this kit (for guidance). */
    sharedComponents?: string[];
    /** All distinct views/displays this Nemo area contains. */
    views: KitView[];
    /** Free-form notes (gotchas, conventions, mock data tips). */
    notes?: string;
}

interface KitModule {
    default?: KitManifest;
    manifest?: KitManifest;
}

const kitModules = import.meta.glob<KitModule>('../kits/*/manifest.ts', {
    eager: true,
});

export interface DiscoveredKit {
    slug: string;
    manifest: KitManifest;
}

/**
 * Discover all kits, optionally filtered by context (size2mix|urbanmap).
 * Omit `context` to get kits from every context (used by the global
 * `/kits` index page).
 */
export function discoverKits(context?: KitContext): DiscoveredKit[] {
    const kits: DiscoveredKit[] = [];
    for (const [path, mod] of Object.entries(kitModules)) {
        const slugMatch = path.match(/\/kits\/([^/]+)\/manifest\.ts$/);
        if (!slugMatch) continue;
        const slug = slugMatch[1];
        const manifest = mod.default ?? mod.manifest;
        if (!manifest) continue;
        if (context && manifest.context !== context) continue;
        kits.push({ slug, manifest });
    }
    return kits.sort((a, b) => a.manifest.name.localeCompare(b.manifest.name));
}
